import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface DeleteConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoId: string;
  videoTitle: string;
  onConfirm?: () => void;
}

export function DeleteConfirmModal({
  open,
  onOpenChange,
  videoId,
  videoTitle,
  onConfirm,
}: DeleteConfirmModalProps) {
  useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const removeVideo = useVideoDashboardStore((state) => state.removeVideo);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para excluir vídeos"));
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      removeVideo(videoId);
      toast.success(translateInterfaceText("Vídeo \"{value0}\" excluído com sucesso", { value0: (videoTitle) }));
      onConfirm?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(translateInterfaceText("Erro ao excluir vídeo"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none p-0 rounded-2xl">
        <AlertDialogHeader className="px-5 py-4">
          <AlertDialogTitle>{translateInterfaceText("Excluir vídeo?")}</AlertDialogTitle>
        </AlertDialogHeader>
        
        <div className="px-5 pb-4">
          <AlertDialogDescription>
            {translateInterfaceText("Tem certeza que deseja excluir permanentemente o vídeo")}{" "}<strong>"{videoTitle}"</strong>{translateInterfaceText("? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
        </div>
        
        <AlertDialogFooter className="px-5 py-4 border-t">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            autoFocus
          >
            {translateInterfaceText("Cancelar")}</Button>
          <Button 
            variant="destructive" 
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {translateInterfaceText("Excluir")}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
